import { computed, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'

import { sum, getUrlParam, PRICING } from '@/utils'
import { useProductsStore, useBundleProductsStore, useCartStore } from './'

import { variantSelectUrlParams } from '@/services/bundle-url-params'

import type { BundlePlan, BundleSelectionMap, BundleProductSelection, BundleSize, BundleType } from '@/types/bundle'
import type { VarietyPackEntry } from '@/types/shopify'

export const useBundleStore = defineStore('bundle', () => {
  const bundleType: BundleType = window.location.pathname.includes('coffee') ? 'coffee' : 'oatmeal'
  const bundleSizes: BundleSize[] = bundleType === 'coffee' ? [ 3, 6 ] : [ 16, 24, 32 ]
  const MIN_SIZE = bundleSizes[0]
  const MAX_SIZE = bundleSizes[bundleSizes.length - 1]

  const productsStore = useProductsStore()
  const bundleProductsStore = useBundleProductsStore()
  const cartStore = useCartStore()

  const { productList } = storeToRefs(productsStore)
  const { onetime, coffeeOnetime, subscription, coffeeSubscription } = storeToRefs(bundleProductsStore)

  const loading = ref(false)
  const onetimeOnly = ref(window.location.href.includes('instant-protein-coffee'))
  const plan = ref<BundlePlan>('subscription')
  const onetimeConfirm = ref(false)
  const checkoutSwitchToSubscription = ref(false)
  const selectedSize = ref<BundleSize>(bundleSizes[0])
  const selection = ref<BundleSelectionMap>({})

  //========================================================================================
  // Computed

  const ready = computed(() => !!onetime.value && !!coffeeOnetime.value &&
    !!subscription.value && !!coffeeSubscription.value && productList.value.length > 0)

  const isSub = computed(() => plan.value === 'subscription')
  const isCheckoutSub = computed(() => isSub.value || checkoutSwitchToSubscription.value)
  const isCoffeeBundle = computed(() => bundleType === 'coffee')

  const totalQuantity = computed(() => sum(...Object.values(selection.value)))

  const quantityBelowMinimum = computed(() => totalQuantity.value < MIN_SIZE)
  const quantityAtMaximum = computed(() => totalQuantity.value === MAX_SIZE)

  const size = computed(() => bundleSizes.find(i => totalQuantity.value <= i) || MIN_SIZE)

  const validSize = computed(() => {
    const quantity = totalQuantity.value
    return quantity === size.value && quantity >= MIN_SIZE
  })

  const product = computed(() => {
    if (isCoffeeBundle.value) {
      return (isCheckoutSub.value ? coffeeSubscription : coffeeOnetime).value
    } else {
      return (isCheckoutSub.value ? subscription : onetime).value
    }
  })

  const findVariant = (size: BundleSize) => {
    return product.value?.variants?.find(i => i.size === size) || product.value?.variants?.[0]
  }

  const variant = computed(() => findVariant(size.value))
  const selectedVariant = computed(() => findVariant(selectedSize.value))


  const selectedPricing = computed(() => PRICING[selectedSize.value])

  const pricing = computed(() => PRICING[size.value])

  const price = computed(() => pricing.value[isCheckoutSub.value ? 'minPrice' : 'onetimePrice'])
  const priceCents = computed(() => pricing.value[isCheckoutSub.value ? 'minCents' : 'onetimeCents'])

  const priceForSize = computed(() => (size: BundleSize) => {
    return product.value?.variants?.find(i => i.size === size)?.price
  })

  const selectedProducts = computed(() => {
    if (!productList.value?.length) {
      return []
    }
    try {
      return Object.entries(selection.value).reverse().map(([ key, quantity ]) => {
        const product = productList.value.find(i => i.key === key)
        if (!product) {
          // delete productList.value[key]
          throw new Error(`Product not found for: ${key}`)
        }
        return {
          key,
          product,
          quantity
        } as BundleProductSelection
      })
    } catch {
      selection.value = {}
      return []
    }
  })

  const bundleItemsAnalyticsData = computed(() => {
    return selectedProducts.value.map((item) => ({
      sku: item.product?.sku,
      quantity: item.quantity
    }))
  })


  const updateQuantity = (key: string, quantity: number) => {
    if (quantity === 0) {
      delete selection.value[key]
    } else {
      selection.value[key] = quantity
    }
  }

  const setSelectionFromContents = (contents: VarietyPackEntry[]) => {
    selection.value = Object.fromEntries(contents.map(i => [ i.product?.key, i.quantity ]).reverse())
  }

  // const incrementQuantityUntilLimit = () => {
  //   updateQuantityUntilValue(MIN_SIZE)
  // }

  // const updateQuantityUntilValue = (value: number) => {
  //   while (totalQuantity.value < value) {
  //     for (const [ key, quantity ] of Object.entries(selection.value)) {
  //       updateQuantity(key, quantity + 1)
  //       if (totalQuantity.value === value) {
  //         break
  //       }
  //     }
  //   }

  //   while (totalQuantity.value > value) {
  //     for (const [ key, quantity ] of Object.entries(selection.value)) {
  //       updateQuantity(key, quantity - 1)
  //       if (totalQuantity.value === value) {
  //         break
  //       }
  //     }
  //   }
  // }

  const readUrlParams = () => {
    const items = getUrlParam('items')
    if (items) {
      setEncodedItems(items)
    } else {
      const { type, size } = variantSelectUrlParams()
      if (type) {
        plan.value = type
      }
      setSelectedSize(size)
    }
  }

  const setSelectedSize = (size?: BundleSize) => {
    if (size && totalQuantity.value <= size) {
      selectedSize.value = size
    }
  }

  const recoverV1Selections = () => {
    if (totalQuantity.value) {
      return
    }
    try {
      const json = window.localStorage.getItem('vuex')
      const data = typeof json === 'string' && JSON.parse(json)
      if (data?.bundler?.items?.length) {
        selection.value = Object.fromEntries(data?.bundler?.items.map(i => {
          try {
            const { quantity, product: { nid, variants } } = i
            const key = `${nid}/${variants[0].nid}`
            return [ key, quantity ]
          } catch {
            // nothing
          }
        }).filter(Boolean))
        window.localStorage.removeItem('vuex')
      }
    } catch (e) {
      // nothing
      console.warn(e)
    }
  }

  const validateSelections = () => {
    if (!productList.value.length) {
      return
    }
    for (const key of Object.keys(selection.value)) {
      const product = productList.value.find(i => i.key === key)
      if (!product) {
        // console.log('> not found', key)
        delete selection.value[key]
      }
    }
  }

  const getEncodedItems = () => {
    const map = Object.entries(selection.value).reduce((acc, [ key, quantity ]) => {
      const id = key.split('/')[0]
      acc[quantity] ||= []
      acc[quantity].push(id)
      return acc
    }, {})
    return window.btoa(new URLSearchParams(map).toString())
  }

  const setEncodedItems = (hash: string) => {
    const map = new URLSearchParams(window.atob(hash))
    map.forEach((idString, quantity) => {
      const ids = idString.split(',')
      ids.forEach(id => {
        const product = productList.value.find(i => i.key.startsWith(id))
        if (product) {
          selection.value[product.key] = Number(quantity)
        }
      })
    })
  }

  const setup = async () => {
    if (ready.value || loading.value) {
      return
    }
    loading.value = true
    await bundleProductsStore.load()
    recoverV1Selections()
    validateSelections()
    loading.value = false
  }

  const reset = () => {
    selection.value = {}
    plan.value = 'subscription'
  }

  watch(totalQuantity, (total: number) => {
    const { value } = selectedSize
    if (total <= value || total >= value) {
      selectedSize.value = size.value
    }
    if (total === 0) {
      cartStore.removeBundle()
    }
  })

  setup()

  return {
    ready,
    onetimeOnly,
    isSub,
    isCoffeeBundle,
    onetimeConfirm,
    selection,
    totalQuantity,
    quantityBelowMinimum,
    quantityAtMaximum,
    validSize,
    size,
    selectedSize,
    selectedPricing,
    plan,
    bundleType,
    bundleSizes,
    pricing,
    price,
    priceCents,
    product,
    variant,
    selectedVariant,
    selectedProducts,
    bundleItemsAnalyticsData,
    checkoutSwitchToSubscription,
    MIN_SIZE,
    MAX_SIZE,
    priceForSize,
    updateQuantity,
    setSelectionFromContents,
    setSelectedSize,
    getEncodedItems,
    setEncodedItems,
    // updateQuantityUntilValue,
    // incrementQuantityUntilLimit,
    readUrlParams,
    setup,
    reset
  }
}, {
  persist: {
    pick: [
      'size',
      'plan',
      'selection'
    ]
  }
})
